import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useGlobalStore } from "@/store/GlobalStore";
import { UserLevel } from "project-config/constants/user-info";
import {
  BalanceText,
  BalanceTextContent,
  CurrentProgress,
  LevelCard,
  MemberProgressBar,
  MemberSystemWrapper,
  NextLevelContent,
  NextLevelText,
  UserName,
  UserNameIcon,
  VertifyLink,
} from "./atom";
import { getAuthStatusTranslate } from "@/pages/profile/helper/getAuthStatusTranslate";
import { redirectToAuth } from "@/pages/profile/helper/redirectToAuth";
import { Space, ToolTip } from "task-eva-ui";
import Normal from "@/assets/images/animation-normal.png";
import Sliver from "@/assets/images/animation-silver.png";
import Gold from "@/assets/images/animation-gold.png";
import Platinum from "@/assets/images/animation-platinum.png";
import Diamond from "@/assets/images/animation-diamond.png";
import DiamondLimit from "@/assets/images/animation-diamond-limit.png";
import DiamondStudent from "@/assets/images/animation-diamond-student.png";
import { useMemberSystem } from "./hooks/useMemberSystem";
import { getMemberLevelTranslate } from "@/pages/profile/helper/getMemberLevelTranslate";
import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import { DesktopRechargeTab } from "../../constants/animation-balance-info";

export function AnimationUserInfoBar() {
  const { t } = useTranslation("topup");
  const { t: profileT } = useTranslation("profile");
  const {
    userInfo,
    isSubAccount,
    isStudent,
    isStudentValid,
    isPersonal,
    studentCertifyInfo,
    userLevel,
    currencySymbol,
    userBalance,
    userDesktopInfo,
  } = useGlobalStore();
  const { currenProgress, progressbar, percent, nextLevel } = useMemberSystem();

  const authStatusTranslate = getAuthStatusTranslate(
    userInfo?.authStatus || 0,
    isStudentValid,
    isPersonal
  );

  const animationLevelContentsMap = new Map<
    UserLevel,
    { img: string; color: string }
  >([
    [
      UserLevel.Normal,
      {
        img: Normal,
        color: "linear-gradient(90deg, #29DD89 1.46%, #17B76A 101.94%)",
      },
    ],
    [
      UserLevel.Sliver,
      {
        img: Sliver,
        color: "linear-gradient(90.44deg, #E7F2FF 0%, #BBCDE8 100%)",
      },
    ],
    [
      UserLevel.Gold,
      {
        img: Gold,
        color:
          "linear-gradient(90.44deg, #FFDFAE 0%, #FFC266 51.56%, #FFCF7E 100%)",
      },
    ],
    [
      UserLevel.Platinum,
      {
        img: Platinum,
        color:
          "linear-gradient(90.44deg, #F4F3FC 0%, #D6C2FF 54.17%, #99B7FF 100%)",
      },
    ],
    [
      UserLevel.Diamond,
      {
        img: isStudent && isStudentValid ? DiamondStudent : Diamond,
        color:
          "linear-gradient(267.35deg, #574B2F 8.68%, #AD7F29 55.67%, #F0CC89 93.34%)",
      },
    ],
    [
      UserLevel.DiamondAnimationGift,
      {
        img: DiamondLimit,
        color:
          "linear-gradient(267.35deg, #574B2F 8.68%, #AD7F29 55.67%, #F0CC89 93.34%)",
      },
    ],
  ]);

  const LevelContent = (level: number) => {
    return animationLevelContentsMap.get(level);
  };
  const location = useLocation();
  const isDesktopTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const activeNameValue = params.get("activeName");
    return activeNameValue === DesktopRechargeTab.Desktop;
  }, [location]);

  return (
    <MemberSystemWrapper>
      <Space gap={16} align={"center"}>
        <UserNameIcon>{userInfo?.userName?.[0].toUpperCase()}</UserNameIcon>
        <UserName>{userInfo?.userName}</UserName>
        {!isSubAccount && authStatusTranslate && (
          <VertifyLink
            onClick={() => {
              redirectToAuth(userInfo, studentCertifyInfo?.studentBlock);
            }}
          >
            {authStatusTranslate}
          </VertifyLink>
        )}
      </Space>
      {isDesktopTab && (
        <>
          <BalanceTextContent>
            <BalanceText>
              <p>{t("topUp_current_desktop")}</p>
              <ToolTip
                content={`${currencySymbol}${userDesktopInfo?.availableAmount}`}
                trigger="hover"
              >
                <span className="symbol">{currencySymbol}</span>
                <span className="credit">
                  {userDesktopInfo?.availableAmount}
                </span>
              </ToolTip>
            </BalanceText>
            {!isStudentValid && (
              <BalanceText className="border-left">
                <p>{t("accumulative_recharge_desktop")}</p>
                <ToolTip
                  content={`${currencySymbol}${userDesktopInfo?.cumulative}`}
                  trigger="hover"
                >
                  <span className="symbol">{currencySymbol}</span>
                  <span className="credit">{userDesktopInfo?.cumulative}</span>
                </ToolTip>
              </BalanceText>
            )}
          </BalanceTextContent>
        </>
      )}
      {!isDesktopTab && (
        <>
          <LevelCard>
            <img src={LevelContent(userLevel)?.img} alt="" />
          </LevelCard>
          <NextLevelContent>
            <MemberProgressBar>
              <CurrentProgress
                percent={percent}
                color={LevelContent(userLevel)?.color}
              />
            </MemberProgressBar>
            {nextLevel >= 0 ? (
              <NextLevelText>
                <span>{profileT("next_level_need_recharge_pre")}</span>&nbsp;
                <span className="vip">
                  {getMemberLevelTranslate(nextLevel)}
                </span>
                &nbsp;
                <span>{profileT("next_level_need_recharge_sub")}</span>&nbsp;
                <span>{` ${currencySymbol}${(
                  progressbar - currenProgress
                ).toFixed(2)}`}</span>
              </NextLevelText>
            ) : isStudentValid ? (
              <NextLevelText>
                <span>{`${profileT("student_end_time")}${dayjs(
                  new Date(userInfo?.studentEndTime || 0)
                ).format("YYYY-MM-DD")}`}</span>
              </NextLevelText>
            ) : userLevel === UserLevel.DiamondAnimationGift ? (
              <NextLevelText>
                <span>{`${profileT("student_end_time")}${dayjs(
                  new Date(userInfo?.tempMemberValidityTime || 0)
                ).format("YYYY-MM-DD")}`}</span>
              </NextLevelText>
            ) : (
              <NextLevelText>{t("animation_level_diamond_tips")}</NextLevelText>
            )}
          </NextLevelContent>
          <BalanceTextContent>
            <BalanceText>
              <p>{t("topUp_current_balance")}</p>
              <ToolTip
                content={`${currencySymbol}${userBalance}`}
                trigger="hover"
              >
                <span className="symbol">{currencySymbol}</span>
                <span className="credit">{userBalance}</span>
              </ToolTip>
            </BalanceText>
            {!isStudentValid && (
              <BalanceText className="border-left">
                <p>{profileT("accumulative_recharge")}</p>
                <ToolTip
                  content={`${currencySymbol}${currenProgress}`}
                  trigger="hover"
                >
                  <span className="symbol">{currencySymbol}</span>
                  <span className="credit">{currenProgress}</span>
                </ToolTip>
              </BalanceText>
            )}
          </BalanceTextContent>
        </>
      )}
    </MemberSystemWrapper>
  );
}
